import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { useLedgerReload } from '../../lib/use-ledger';
import { useAuth } from '../../contexts/AuthContext';
import { applyTableQuery, columnsMatch, nextSort, type ColumnSort } from '../../lib/list-query';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../../components/ui/ColumnSearch';
import { Button } from '../../components/ui/Button';
import { useNotice } from '../../components/ui/Notice';
import { ExcelButton, PageHead, SortHeader } from '../../components/ui/chrome';
import { Input } from '../../components/ui/Input';

interface ReportRow { id: string; title: string; visibility: string; ownerEmail: string; spec: { query: string; filters?: Record<string, unknown> } }
interface OpenData { report: { spec: { query: string; filters?: Record<string, unknown> } }; data?: { kpis?: { label: string; value: unknown }[]; columns?: string[]; rows?: Record<string, unknown>[]; drill?: { query: string; paramFrom: string } } }

export function ReportLayouts() {
  const { user } = useAuth();
  const notice = useNotice();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'mine' | 'shared' | 'org'>('mine');
  const [openData, setOpenData] = useState<OpenData | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ColumnSort | null>(null);
  const columnSearch = useColumnSearch();
  const query = useQuery({ queryKey: ['reports'], queryFn: () => apiGet<ReportRow[]>('/api/reports') });
  function reload() { void queryClient.invalidateQueries({ queryKey: ['reports'] }); }
  useLedgerReload('reports', reload);
  const filtered = useMemo(() => (query.data || []).filter((row) => {
    if (tab === 'mine') return row.ownerEmail === user?.email;
    if (tab === 'org') return row.visibility === 'org';
    return row.visibility === 'shared';
  }), [query.data, tab, user?.email]);

  async function drill(data: OpenData, row: Record<string, unknown>) {
    const spec = data.data?.drill;
    if (!spec) return;
    const filters = { ...data.report.spec.filters, [spec.paramFrom]: row[spec.paramFrom] || row.customerId };
    const next = await apiPost<OpenData['data']>('/api/reports/preview', { query: spec.query, filters });
    setSearch('');
    setSort(null);
    columnSearch.clear();
    setOpenData({ report: { spec: { query: spec.query, filters } }, data: next });
  }

  const reportColumns = (openData?.data?.columns || []).map((col) => ({ key: col, text: (row: Record<string, unknown>) => row[col] ?? '' }));
  const reportRows = applyTableQuery(openData?.data?.rows || [], search, reportColumns, sort).filter((row) => columnsMatch(Object.fromEntries(reportColumns.map((column) => [column.key, String(column.text(row) ?? '')])), columnSearch.applied));
  useEffect(() => { columnSearch.clear(); }, [openData?.data?.columns?.join('|'), columnSearch.clear]);

  return (
    <div className="space-y-4">
      <PageHead crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Saved layouts' }]} title="Saved layouts" subtitle={`${filtered.length} saved report layouts`} />
      <div className="flex gap-2">
        {(['mine', 'shared', 'org'] as const).map((item) => (
          <Button key={item} size="sm" variant={tab === item ? 'default' : 'outline'} onClick={() => setTab(item)}>{item[0].toUpperCase() + item.slice(1)}</Button>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {filtered.map((row) => (
          <div key={row.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="font-semibold">{row.title}</div>
            <div className="text-xs text-gray-500">{row.visibility} · {row.spec.query}</div>
            <Button size="xs" className="mt-2" variant="outline" onClick={() => { setSearch(''); setSort(null); columnSearch.clear(); void apiGet<OpenData>(`/api/reports/${row.id}`).then(setOpenData); }}>Open live</Button>
            <Button size="xs" className="mt-2" variant="ghost" onClick={() => void notice.run('Sharing the report', async () => { await apiPost(`/api/reports/${row.id}/visibility`, { visibility: 'org' }); reload(); }, 'Report shared with the organisation', { confirm: 'This shares the report layout with the organisation. It does not share your data rights.' })}>Share org</Button>
          </div>
        ))}
      </div>
      {openData ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="mb-3 flex flex-wrap gap-2">
            {(openData.data?.kpis || []).map((kpi) => <div key={kpi.label} className="rounded bg-gray-200 px-3 py-2 text-sm dark:bg-gray-700">{kpi.label}: {String(kpi.value)}</div>)}
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <Input className="h-8 max-w-xs" aria-label="Search report" placeholder="Search report" value={search} onChange={(event) => setSearch(event.target.value)} />
            <ExcelButton filename={openData.report.spec.query || 'Report'} headers={openData.data?.columns || []} rows={reportRows.map((row) => (openData.data?.columns || []).map((col) => {
              const value = row[col];
              return typeof value === 'number' ? value : String(value ?? '');
            }))} />
          </div>
          <table className="bo-cards min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500 dark:border-gray-700">{(openData.data?.columns || []).map((col) => <SortHeader key={col} className="py-2 text-left font-medium" label={col} active={sort?.key === col} dir={sort?.dir || 'asc'} onSort={() => setSort((current) => nextSort(current, col))} />)}<th className="w-8 text-right"><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th></tr>
              {columnSearch.open ? <ColumnFilterInputs columns={(openData.data?.columns || []).map((col) => ({ key: col, label: col }))} draft={columnSearch.draft} onChange={columnSearch.set} trailing /> : null}
            </thead>
            <tbody>
              {reportRows.map((row, index) => (
                <tr key={index} className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700" onClick={() => void drill(openData, row)}>
                  {(openData.data?.columns || []).map((col) => <td key={col} data-label={col}>{String(row[col] ?? '')}</td>)}
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
